import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError, formatDateTR } from "../lib/apiClient";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";

const VIEWS = [
  { key: "all",    label: "Genel Liste",      cats: ["Baza", "Başlık", "Yatak", "Komodin", "Bench"] },
  { key: "baza",   label: "Baza Bölümü",      cats: ["Baza"] },
  { key: "yatak",  label: "Yatak Bölümü",     cats: ["Yatak"] },
  { key: "baslik", label: "Başlık Bölümü",    cats: ["Başlık", "Komodin", "Bench"] },
];

export default function ProductionListPrintPage() {
  const [params] = useSearchParams();
  const ids = params.get("ids") || "";
  const [preview, setPreview] = useState(null);
  const [view, setView] = useState("all");

  useEffect(() => {
    if (!ids) return;
    (async () => {
      try {
        const { data } = await api.get(`/production-lists/preview`, { params: { ids } });
        setPreview(data);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, [ids]);

  // Flatten: one row per (order,size) group with category cells
  const rows = useMemo(() => {
    if (!preview) return [];
    const list = [];
    preview.orders.forEach(o => {
      // Group products of an order by (size, fabric) so multi-size orders split
      const groups = {};
      (o.products || []).forEach(p => {
        const key = `${p.size || ""}||${p.fabric || ""}||${p.quantity || 0}`;
        if (!groups[key]) {
          groups[key] = {
            order: o,
            size: p.size || "",
            fabric: p.fabric || "",
            quantity: p.quantity || 0,
            cells: { Baza: "", "Başlık": "", Yatak: "", Komodin: "", Bench: "" },
            notes_by_cat: {},
          };
        }
        groups[key].cells[p.category] = p.model || "•";
        if (p.notes) groups[key].notes_by_cat[p.category] = p.notes;
      });
      Object.values(groups).forEach(g => list.push(g));
    });
    return list;
  }, [preview]);

  const cur = VIEWS.find(v => v.key === view) || VIEWS[0];
  const filteredRows = useMemo(() => {
    if (view === "all") return rows;
    return rows.filter(r => cur.cats.some(c => (r.cells[c] || "").trim()));
  }, [rows, view]);

  const totals = useMemo(() => {
    const t = { rows: filteredRows.length, qty: 0, pieces: 0 };
    filteredRows.forEach(r => {
      t.qty += r.quantity;
      cur.cats.forEach(c => { if ((r.cells[c] || "").trim()) t.pieces += r.quantity; });
    });
    return t;
  }, [filteredRows, view]);

  const doPrint = () => {
    window.print();
  };

  if (!ids) return <div className="p-8 text-slate-500">Siparişler sayfasından seçim yapıp buraya gelin.</div>;
  if (!preview) return <div className="p-8 text-slate-500">Yükleniyor...</div>;

  return (
    <div className="print-page">
      {/* Toolbar - hidden on print */}
      <div className="print-hidden sticky top-0 z-30 bg-white border-b-2 border-slate-900 p-4 flex flex-wrap gap-2 items-center">
        <Link to={`/uretim-listesi?ids=${ids}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-blue-700 mr-3">
          <ArrowLeft size={16}/> Üretim Listesine Dön
        </Link>
        {VIEWS.map(v => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            data-testid={`plprint-view-${v.key}`}
            className={`px-4 py-2 border-2 text-sm font-bold uppercase ${view === v.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-slate-800"}`}
          >
            {v.label}
          </button>
        ))}
        <button onClick={doPrint} data-testid="plprint-btn"
          className="ml-auto inline-flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm">
          <Printer size={16}/> Yazdır / PDF
        </button>
      </div>

      <div className="print-sheet p-6 max-w-[1600px] mx-auto">
        <header className="mb-4 border-b-4 border-blue-700 pb-3">
          <div className="flex items-baseline justify-between gap-4">
            <div className="flex items-baseline gap-3">
              <span className="inline-block bg-blue-700 text-white font-display font-black text-xl px-3 py-1 leading-none">M</span>
              <div>
                <div className="font-display font-black text-3xl text-blue-700 leading-none tracking-tight">MARSELLA</div>
                <div className="font-display font-bold text-sm text-slate-700 tracking-[0.25em] uppercase mt-0.5">Bedding · Üretim Listesi</div>
              </div>
            </div>
            <div className="text-right text-xs text-slate-700">
              <div className="font-display font-black text-lg text-slate-900">{cur.label}</div>
              <div><b>Tarih:</b> {formatDateTR(new Date().toISOString())}</div>
              <div><b>Sipariş:</b> {preview.orders.length} · <b>Satır:</b> {totals.rows} · <b>Set:</b> {totals.qty}</div>
            </div>
          </div>
        </header>

        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-100 border-b-2 border-slate-900 text-xs uppercase tracking-wider">
              <th className="text-left px-2 py-2 border border-slate-400">Sipariş</th>
              <th className="text-left px-2 py-2 border border-slate-400">Müşteri</th>
              <th className="text-left px-2 py-2 border border-slate-400">Referans</th>
              <th className="text-left px-2 py-2 border border-slate-400">Ölçü</th>
              <th className="text-left px-2 py-2 border border-slate-400">Kumaş / Renk</th>
              <th className="text-center px-2 py-2 border border-slate-400">Adet</th>
              {cur.cats.map(c => (
                <th key={c} className="text-left px-2 py-2 border border-slate-400">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr><td colSpan={6 + cur.cats.length} className="text-center py-8 text-slate-500">Bu bölüm için ürün yok.</td></tr>
            )}
            {filteredRows.map((r, i) => {
              // Aynı siparişin sadece ilk satırında order-level notu göster
              const isFirstOfOrder = i === 0 || filteredRows[i - 1].order.id !== r.order.id;
              const orderNote = isFirstOfOrder ? (r.order.notes || "") : "";
              return (
                <React.Fragment key={i}>
                  <tr className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                    <td className="px-2 py-1.5 border border-slate-300 font-mono font-bold text-blue-800 whitespace-nowrap">{r.order.order_number}</td>
                    <td className="px-2 py-1.5 border border-slate-300 font-semibold">{r.order.customer_name}</td>
                    <td className="px-2 py-1.5 border border-slate-300 font-mono">{r.order.reference || "—"}</td>
                    <td className="px-2 py-1.5 border border-slate-300 font-mono">{r.size || "—"}</td>
                    <td className="px-2 py-1.5 border border-slate-300">{r.fabric || "—"}</td>
                    <td className="px-2 py-1.5 border border-slate-300 text-center font-mono font-bold">{r.quantity}</td>
                    {cur.cats.map(c => (
                      <td key={c} className={`px-2 py-1.5 border border-slate-300 ${r.cells[c] ? "font-semibold" : "text-slate-300"}`}>
                        {r.cells[c] || "—"}
                        {r.notes_by_cat[c] && <div className="text-[10px] italic text-slate-500">{r.notes_by_cat[c]}</div>}
                      </td>
                    ))}
                  </tr>
                  {orderNote && (
                    <tr className="bg-amber-50">
                      <td colSpan={6 + cur.cats.length} className="px-2 py-1 border border-amber-300 text-xs text-amber-900">
                        <b className="uppercase tracking-widest text-[9px] mr-1">Not:</b>{orderNote}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        <div className="print-hidden mt-6 p-3 bg-amber-50 border-2 border-amber-400 text-xs text-amber-900">
          <b>📄 Yazdırma ipucu:</b> Yazdır penceresi açıldığında <b>"Diğer ayarlar" → "Üst bilgi ve alt bilgi"</b> seçeneğini <b>kapatın</b> ki sayfa altında web adresi çıkmasın.
        </div>
      </div>

      <style>{`
        @media print {
          .print-hidden { display: none !important; }
          body, html { background: white !important; margin: 0 !important; }
          .print-sheet { padding: 6mm !important; max-width: none !important; }
          table { font-size: 10pt !important; }
          @page { size: A4 landscape; margin: 6mm; }
          aside, .card-industrial { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
